import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, ProgressBar } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import DatePicker from "components/common/DatePicker";
import BackDropLoader from "components/common/BackDropLoader";
import RecipeSummary from "./RecipeSummary";
import RecipeUsageService from "services/RecipeUsageService";
import {
  RECIPE_USAGE_SAVE_ERROR,
  PLEASE_SELECT_RECIPE,
  PLEASE_SELECT_APPLICATION_DATE,
  PLEASE_ENTER_SPRAYED_ACRES,
  SPRAYED_ACRES_CANNOT_EXCEED_TOTAL_ACRES,
  LABEL_SELECT_RECIPE,
  PLEASE_ENTER_WATER_QUANTITY,
  LABEL_ENTER_WATER_QUANTITY,
  SPRAYED_ACRES_MUST_BE_GREATER_THAN_ZERO,
  WATER_QUANTITY_MUST_BE_GREATER_THAN_ZERO,
  ADD_RECIPE_USAGE,
  VIEW_RECIPE_USAGE,
  EDIT_RECIPE_USAGE,
} from "configurations/MessageConstants";
import {
  setRecipeUsages,
  setSavedRecipeUsage,
  setSaveRecipeUsageSuccess,
  setRecipeUsageMode,
} from "reduxStore/slices/recipeUsageSlice";
import { DATE_FORMAT, MODE_TYPES } from "configurations/Constants";
import { format } from "date-fns";
import { toast } from "react-toastify";
import { InputHelper } from "utils/InputHelper";

const RecipeUsageForm = ({ mode, show, onClose }) => {
  const dispatch = useDispatch();
  const [isEditingRecipe, setIsEditingRecipe] = useState(false);
  const editedRecipeUsage = useSelector(
    (state) => state.recipeUsage.editedRecipeUsage
  );
  const selectedPlantedAcre = useSelector(
    (state) => state.plantedAcres.selectedPlantedAcre
  );
  const selectedSite = useSelector((state) => state.site.selectedSite);
  const recipes = useSelector((state) => state.recipe.recipes);
  const recipeUsages = useSelector((state) => state.recipeUsage.recipeUsages);
  const totalAcres = selectedPlantedAcre?.acres;
  const isSharedViewOnly = selectedSite.isShared && !selectedSite.editor;
  const recipeList = recipes?.filter((recipe) => !recipe.archived);

  const defaultValues = {
    recipeId: "",
    applicationDate: null,
    sprayedAcres: "",
    quantity: "",
  };

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({ defaultValues });

  const sprayedAcres = watch("sprayedAcres");
  const selectedRecipeId = watch("recipeId");

  useEffect(() => {
    if (show && mode === MODE_TYPES.EDIT && editedRecipeUsage) {
      reset({
        recipeId:
          recipeList.find((recipe) => recipe.id === editedRecipeUsage.recipeId)
            ?.id || "",
        applicationDate: editedRecipeUsage.applicationDate
          ? new Date(editedRecipeUsage.applicationDate)
          : null,
        sprayedAcres: editedRecipeUsage.sprayedAcres,
        quantity: editedRecipeUsage.quantity,
      });
      setIsEditingRecipe(false);
    }
    dispatch(setRecipeUsageMode(mode || MODE_TYPES.ADD));
  }, [show, mode, editedRecipeUsage, dispatch, reset]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        id: editedRecipeUsage?.id,
        recipeId: data.recipeId,
        applicationDate: format(data.applicationDate, DATE_FORMAT),
        sprayedAcres: data.sprayedAcres,
        quantity: data.quantity,
      };
      const response =
        mode === MODE_TYPES.ADD
          ? await RecipeUsageService.addRecipeUsage(
              selectedSite.id,
              selectedPlantedAcre.id,
              payload
            )
          : await RecipeUsageService.updateRecipeUsage(
              selectedSite.id,
              selectedPlantedAcre.id,
              editedRecipeUsage.id,
              payload
            );
      handleSave(response);
      dispatch(
        setRecipeUsages(
          mode === MODE_TYPES.ADD
            ? [response, ...recipeUsages]
            : recipeUsages.map((recipeUsage) =>
                recipeUsage.id === response.id ? response : recipeUsage
              )
        )
      );
      handleClose();
    } catch (error) {
      console.error(RECIPE_USAGE_SAVE_ERROR, error);
      toast.error(error.response.data.detail);
    }
  };

  const handleSave = (recipeUsage) => {
    dispatch(setSavedRecipeUsage(recipeUsage));
    dispatch(setSaveRecipeUsageSuccess(true));
  };

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      fullscreen="sm-down"
      aria-labelledby="recipe-usage-form-popup"
    >
      {isSubmitting && <BackDropLoader />}
      <Modal.Header closeButton>
        <Modal.Title id="recipe-usage-form-popup">
          {mode === MODE_TYPES.ADD
            ? ADD_RECIPE_USAGE
            : isSharedViewOnly
            ? VIEW_RECIPE_USAGE
            : EDIT_RECIPE_USAGE}
        </Modal.Title>
      </Modal.Header>
      <Form noValidate onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Modal.Body>
          <Row>
            <Col className="mb-4" xs={12} md={4}>
              <Form.Group>
                <Form.Label>Recipe</Form.Label>
                <div>
                  {mode === MODE_TYPES.EDIT &&
                  !isEditingRecipe &&
                  editedRecipeUsage ? (
                    <div
                      className="position-relative"
                      style={{
                        cursor: isSharedViewOnly ? "default" : "pointer",
                      }}
                      onClick={() =>
                        !isSharedViewOnly && setIsEditingRecipe(true)
                      }
                    >
                      <Form.Control
                        type="text"
                        value={editedRecipeUsage.recipeName}
                        disabled
                        className="input-underline"
                      />
                      {!isSharedViewOnly && (
                        <i className="fa fa-edit position-absolute end-0 top-50 translate-middle-y p-2 text-muted"></i>
                      )}
                    </div>
                  ) : (
                    <Controller
                      name="recipeId"
                      control={control}
                      rules={{ required: PLEASE_SELECT_RECIPE }}
                      render={({ field }) => (
                        <Form.Select
                          {...field}
                          className="input-underline"
                          aria-label="Select Recipe"
                          isInvalid={!!errors.recipeId}
                          disabled={isSharedViewOnly}
                        >
                          <option value="">{LABEL_SELECT_RECIPE}</option>
                          {recipeList.map((recipe) => (
                            <option key={recipe.id} value={recipe.id}>
                              {recipe.name}{" "}
                              {recipe.version > 1 && `(v${recipe.version})`}
                            </option>
                          ))}
                        </Form.Select>
                      )}
                    />
                  )}
                  <Form.Control.Feedback type="invalid">
                    {errors.recipeId?.message}
                  </Form.Control.Feedback>
                </div>
              </Form.Group>
            </Col>
            <div className="d-block d-md-none">
              <RecipeSummary
                recipeId={selectedRecipeId}
                editedRecipeUsage={editedRecipeUsage}
              />
            </div>
            <Col className="mb-4" xs={12} md={4}>
              <Form.Group>
                <Controller
                  name="applicationDate"
                  control={control}
                  rules={{ required: PLEASE_SELECT_APPLICATION_DATE }}
                  render={({ field }) => (
                    <DatePicker
                      label="Application Date"
                      date={field.value}
                      onChange={field.onChange}
                      isInvalid={!!errors.applicationDate}
                      disabled={isSharedViewOnly}
                      handleDisabledDates={(date) => date > new Date()}
                    />
                  )}
                />
                {errors.applicationDate && (
                  <div className="custom-invalid-feedback mt-1">
                    {errors.applicationDate.message}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label>Water Quantity (G/A)</Form.Label>
                <Controller
                  name="quantity"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_WATER_QUANTITY,
                    validate: (value) =>
                      Number(value) > 0 ||
                      WATER_QUANTITY_MUST_BE_GREATER_THAN_ZERO,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      className="input-underline"
                      isInvalid={!!errors.quantity}
                      disabled={isSharedViewOnly}
                      placeholder={LABEL_ENTER_WATER_QUANTITY}
                      maxLength={10}
                      onChange={(e) => {
                        InputHelper.validateFloatNumberInput(e, field.onChange);
                      }}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.quantity?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <div className="d-none d-md-block">
            <RecipeSummary
              recipeId={selectedRecipeId}
              editedRecipeUsage={editedRecipeUsage}
            />
          </div>
          <Row>
            <Col className="mb-4" xs={12} md={6}>
              <Form.Group>
                <Form.Label>Total Acres</Form.Label>
                <Form.Control
                  type="text"
                  value={totalAcres}
                  disabled
                  className="input-underline"
                />
              </Form.Group>
            </Col>
            <Col className="mb-4" xs={12} md={6}>
              <Form.Group>
                <Form.Label>Sprayed Acres</Form.Label>
                <Controller
                  name="sprayedAcres"
                  control={control}
                  rules={{
                    required: PLEASE_ENTER_SPRAYED_ACRES,
                    validate: (value) =>
                      Number(value) === 0
                        ? SPRAYED_ACRES_MUST_BE_GREATER_THAN_ZERO
                        : Number(value) > totalAcres
                        ? SPRAYED_ACRES_CANNOT_EXCEED_TOTAL_ACRES
                        : true,
                  }}
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      type="text"
                      className="input-underline"
                      isInvalid={!!errors.sprayedAcres}
                      disabled={isSharedViewOnly}
                      maxLength={10}
                      onChange={(e) => {
                        InputHelper.validateFloatNumberInput(e, field.onChange);
                      }}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.sprayedAcres?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <ProgressBar
          now={sprayedAcres}
          max={totalAcres}
          className={`add-recipe-usage-progress-bar ${
            Number(sprayedAcres) >= Number(totalAcres)
              ? "progress-green"
              : "progress-yellow"
          }`}
        />
        {!isSharedViewOnly && (
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {mode === MODE_TYPES.ADD ? "Add" : "Update"}
            </Button>
          </Modal.Footer>
        )}
      </Form>
    </Modal>
  );
};

export default RecipeUsageForm;
